import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError, AxiosResponse } from "axios";
import { getInhouseAnonymousId } from "src/utils/anonymousId";

const BASE_ENDPOINT = process.env.REACT_APP_BACKEND_BASE_ENDPOINT;

// ─── API contracts ────────────────────────────────────────────────────────────
//
// Shapes captured from real responses (see openspec change
// `adopt-typescript-incrementally` for the captures). The /api/user/memory
// endpoint and all /api/user/{update-memory,memory}/{...} mutation endpoints
// return MemoryResponse uniformly (full state post-mutation).

export interface Personal {
  name: string | null;
  location: string | null;
  context: string | null;
}

export interface Business {
  id: string;
  name: string;
  location: string | null;
  website: string | null;
  relationship: string;
  context: string;
}

export interface RelatedEntity {
  id: string;
  type: "company" | "person" | string;
  name: string;
  location: string | null;
  website: string | null;
  relationship: string;
}

export interface MemoryResponse {
  version: number;
  personal: Personal;
  businesses: Business[];
  related_entities: RelatedEntity[];
  updated_at: string;
}

export interface MiscMetadataResponse {
  memory_information?: {
    is_memory_modal_shown?: boolean;
    is_memory_banner_shown?: boolean;
    has_saved_memory?: boolean;
  };
}

// Request body shapes for the PATCH endpoints. Inferred from how the slice
// passes data through without transformation. FIXME(<linear-ticket-tbd>):
// tighten once backend OpenAPI / Pydantic models are accessible.
export type UpdatePersonalMemoryRequest = Partial<Personal>;
export type UpdateBusinessRequest = { businessId: string } & Partial<Omit<Business, "id">>;
export type DeleteBusinessRequest = { businessId: string };
export type UpdateRelatedEntityRequest = { entityId: string } & Partial<Omit<RelatedEntity, "id">>;
export type DeleteRelatedEntityRequest = { entityId: string };
export type PatchMiscMetadataRequest = Record<string, unknown>;

// 412 conflict error body — server returns either { detail: { current } } or { current }.
interface ConflictErrorData {
  detail?: { current?: MemoryResponse };
  current?: MemoryResponse;
}

// ─── State ────────────────────────────────────────────────────────────────────

type Status = "idle" | "loading" | "success" | "failed";

export interface ProfileMemoryState {
  version: number | null;
  personal: Personal | Record<string, never>;
  businesses: Business[];
  relatedEntities: RelatedEntity[];
  updatedAt: string | null;
  showPanel: boolean;
  fetchStatus: Status;
  updateStatus: Status;
  error: number | string | null;
  miscMetadata: {
    isMemoryModalShown: boolean;
    isMemoryBannerShown: boolean;
    hasSavedMemory: boolean;
  };
  miscMetadataStatus: Status;
}

interface MutationFulfilledPayload {
  response: MemoryResponse;
}
interface MutationRejectedPayload {
  error?: unknown;
  status?: number | string;
  conflict?: boolean;
  current?: MemoryResponse;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type AuthHeaders = { Authorization: string } | { "anonymous-user-id": string } | Record<string, never>;

const getAuthHeaders = (): AuthHeaders => {
  const accessToken = localStorage.getItem("accessToken2");
  if (accessToken) return { Authorization: `Bearer ${accessToken}` };
  // FIXME(<linear-ticket-tbd>): pre-existing bug found by TS migration —
  // getInhouseAnonymousId is async and returns a Promise. The original JS
  // code never awaited it, so the header value has always been the literal
  // Promise object (stringified as "[object Promise]") in this code path.
  // Preserving current behavior; tracked for fix in a separate openspec change.
  const anonymousId = getInhouseAnonymousId() as unknown as string | null;
  if (anonymousId) return { "anonymous-user-id": anonymousId };
  return {};
};

const applyResponse = (state: ProfileMemoryState, response: MemoryResponse): void => {
  state.version = response.version;
  state.personal = response.personal || {};
  state.businesses = response.businesses || [];
  state.relatedEntities = response.related_entities || [];
  state.updatedAt = response.updated_at;
};

/**
 * On 412 (version conflict), extract the server's current state, update the
 * local version, and retry the request once. If the retry also fails, reject
 * with the conflict payload.
 */
const retryOn412 = async (
  requestFn: (version: number | null) => Promise<AxiosResponse<MemoryResponse>>,
  error: unknown,
  thunkAPI: { rejectWithValue: (value: MutationRejectedPayload) => unknown },
): Promise<{ response: MemoryResponse } | unknown> => {
  const axiosErr = error as AxiosError<ConflictErrorData>;
  const status = axiosErr.response?.status;
  if (status !== 412) {
    return thunkAPI.rejectWithValue({ error, status });
  }

  const current = axiosErr.response?.data?.detail?.current ?? axiosErr.response?.data?.current;
  console.log("[retryOn412] 412 received. Server current:", current, "Response data:", axiosErr.response?.data);
  if (current?.version == null) {
    console.log("[retryOn412] No version in server response, giving up");
    // FIXME(<linear-ticket-tbd>): when `current` is undefined, downstream
    // onMutationRejected calls applyResponse(state, undefined) and crashes.
    // Pre-existing bug — fix in a separate openspec change.
    return thunkAPI.rejectWithValue({ conflict: true, current });
  }

  console.log("[retryOn412] Retrying with version:", current.version);
  try {
    const response = await requestFn(current.version);
    console.log("[retryOn412] Retry succeeded:", response.data);
    return { response: response.data };
  } catch (retryError) {
    const retryAxiosErr = retryError as AxiosError<ConflictErrorData>;
    console.log("[retryOn412] Retry also failed:", retryAxiosErr.response?.status, retryAxiosErr.response?.data);
    const retryCurrent =
      retryAxiosErr.response?.status === 412
        ? (retryAxiosErr.response.data?.detail?.current ?? retryAxiosErr.response.data?.current)
        : current;
    return thunkAPI.rejectWithValue({ conflict: true, current: retryCurrent });
  }
};

// ─── Misc Metadata Thunks ─────────────────────────────────────────────────────

export const fetchMiscMetadata = createAsyncThunk<MiscMetadataResponse>(
  "profileMemory/fetchMiscMetadata",
  async (_, thunkAPI) => {
    const headers = getAuthHeaders();
    try {
      const response = await axios.get<MiscMetadataResponse>(`${BASE_ENDPOINT}/api/user/misc-metadata`, { headers });
      return response.data;
    } catch (error) {
      const axiosErr = error as AxiosError;
      return thunkAPI.rejectWithValue({ error, status: axiosErr.response?.status });
    }
  },
);

export const patchMiscMetadata = createAsyncThunk<MiscMetadataResponse, PatchMiscMetadataRequest>(
  "profileMemory/patchMiscMetadata",
  async (data, thunkAPI) => {
    const headers = getAuthHeaders();
    try {
      const response = await axios.patch<MiscMetadataResponse>(`${BASE_ENDPOINT}/api/user/misc-metadata`, data, {
        headers,
      });
      return response.data;
    } catch (error) {
      const axiosErr = error as AxiosError;
      return thunkAPI.rejectWithValue({ error, status: axiosErr.response?.status });
    }
  },
);

// ─── Memory Thunks ────────────────────────────────────────────────────────────

export const fetchProfileMemory = createAsyncThunk<{ response: MemoryResponse }>(
  "profileMemory/fetchProfileMemory",
  async (_, thunkAPI) => {
    const headers = getAuthHeaders();
    try {
      const response = await axios.get<MemoryResponse>(`${BASE_ENDPOINT}/api/user/memory`, { headers });
      return { response: response.data };
    } catch (error) {
      const axiosErr = error as AxiosError;
      return thunkAPI.rejectWithValue({ error, status: axiosErr.response?.status });
    }
  },
);

// Shared mutation-thunk shape. Each mutation: read current version from state,
// PATCH/DELETE with an If-Match header, retry once on 412 with the server-current
// version. All return { response: MemoryResponse } on fulfilled.
//
// Note: version is allowed to be null and flows through to makeRequest as-is —
// matches original JS behavior (backend 412s on null, retryOn412 syncs from
// server-current). Adding a defensive null-guard would be a behavior change.

type ThunkApi = { getState: () => unknown; rejectWithValue: (v: MutationRejectedPayload) => unknown };

const dispatchMutation = async (
  makeRequest: (version: number | null) => Promise<AxiosResponse<MemoryResponse>>,
  thunkAPI: ThunkApi,
): Promise<{ response: MemoryResponse } | unknown> => {
  try {
    const version = (thunkAPI.getState() as { profileMemory: ProfileMemoryState }).profileMemory.version;
    const response = await makeRequest(version);
    return { response: response.data };
  } catch (error) {
    return retryOn412(makeRequest, error, thunkAPI);
  }
};

export const updatePersonalMemory = createAsyncThunk<
  { response: MemoryResponse },
  UpdatePersonalMemoryRequest,
  { rejectValue: MutationRejectedPayload }
>("profileMemory/updatePersonalMemory", async (data, thunkAPI) => {
  const makeRequest = (ver: number | null) => {
    const headers = { ...getAuthHeaders(), "If-Match": String(ver) };
    return axios.patch<MemoryResponse>(`${BASE_ENDPOINT}/api/user/update-memory/personal`, data, { headers });
  };
  return (await dispatchMutation(makeRequest, thunkAPI)) as { response: MemoryResponse };
});

export const updateBusiness = createAsyncThunk<
  { response: MemoryResponse },
  UpdateBusinessRequest,
  { rejectValue: MutationRejectedPayload }
>("profileMemory/updateBusiness", async ({ businessId, ...data }, thunkAPI) => {
  const makeRequest = (ver: number | null) => {
    const headers = { ...getAuthHeaders(), "If-Match": String(ver) };
    return axios.patch<MemoryResponse>(`${BASE_ENDPOINT}/api/user/update-memory/businesses/${businessId}`, data, {
      headers,
    });
  };
  return (await dispatchMutation(makeRequest, thunkAPI)) as { response: MemoryResponse };
});

export const deleteBusiness = createAsyncThunk<
  { response: MemoryResponse },
  DeleteBusinessRequest,
  { rejectValue: MutationRejectedPayload }
>("profileMemory/deleteBusiness", async ({ businessId }, thunkAPI) => {
  const makeRequest = (ver: number | null) => {
    const headers = { ...getAuthHeaders(), "If-Match": String(ver) };
    return axios.delete<MemoryResponse>(`${BASE_ENDPOINT}/api/user/memory/businesses/${businessId}`, { headers });
  };
  return (await dispatchMutation(makeRequest, thunkAPI)) as { response: MemoryResponse };
});

export const updateRelatedEntity = createAsyncThunk<
  { response: MemoryResponse },
  UpdateRelatedEntityRequest,
  { rejectValue: MutationRejectedPayload }
>("profileMemory/updateRelatedEntity", async ({ entityId, ...data }, thunkAPI) => {
  const makeRequest = (ver: number | null) => {
    const headers = { ...getAuthHeaders(), "If-Match": String(ver) };
    return axios.patch<MemoryResponse>(`${BASE_ENDPOINT}/api/user/update-memory/related-entities/${entityId}`, data, {
      headers,
    });
  };
  return (await dispatchMutation(makeRequest, thunkAPI)) as { response: MemoryResponse };
});

export const deleteRelatedEntity = createAsyncThunk<
  { response: MemoryResponse },
  DeleteRelatedEntityRequest,
  { rejectValue: MutationRejectedPayload }
>("profileMemory/deleteRelatedEntity", async ({ entityId }, thunkAPI) => {
  const makeRequest = (ver: number | null) => {
    const headers = { ...getAuthHeaders(), "If-Match": String(ver) };
    return axios.delete<MemoryResponse>(`${BASE_ENDPOINT}/api/user/memory/related-entities/${entityId}`, { headers });
  };
  return (await dispatchMutation(makeRequest, thunkAPI)) as { response: MemoryResponse };
});

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: ProfileMemoryState = {
  version: null,
  personal: {},
  businesses: [],
  relatedEntities: [],
  updatedAt: null,
  showPanel: false,
  fetchStatus: "idle",
  updateStatus: "idle",
  error: null,
  miscMetadata: {
    isMemoryModalShown: false,
    isMemoryBannerShown: false,
    hasSavedMemory: false,
  },
  miscMetadataStatus: "idle",
};

const profileMemorySlice = createSlice({
  name: "profileMemory",
  initialState,
  reducers: {
    setShowProfileMemoryPanel: (state, action: PayloadAction<boolean>) => {
      state.showPanel = action.payload;
    },
    updateVersionFromWS: (state, action: PayloadAction<number>) => {
      state.version = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileMemory.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchProfileMemory.fulfilled, (state, action) => {
        state.fetchStatus = "success";
        applyResponse(state, action.payload.response);
      })
      .addCase(fetchProfileMemory.rejected, (state, action) => {
        state.fetchStatus = "failed";
        const payload = action.payload as MutationRejectedPayload | undefined;
        state.error = payload?.status ?? "unknown";
      });

    const onMutationFulfilled = (state: ProfileMemoryState, action: PayloadAction<MutationFulfilledPayload>) => {
      state.updateStatus = "idle";
      applyResponse(state, action.payload.response);
    };
    const onMutationRejected = (state: ProfileMemoryState, action: { payload?: MutationRejectedPayload }) => {
      state.updateStatus = "idle";
      if (action.payload?.conflict) {
        // FIXME(<linear-ticket-tbd>): action.payload.current may be undefined
        // when 412 returns no current. applyResponse(state, undefined) crashes.
        // Pre-existing bug — to be fixed in a separate openspec change.
        applyResponse(state, action.payload.current as MemoryResponse);
      } else {
        state.error = action.payload?.status ?? "unknown";
      }
    };

    builder
      .addCase(updatePersonalMemory.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updatePersonalMemory.fulfilled, onMutationFulfilled)
      .addCase(updatePersonalMemory.rejected, onMutationRejected);

    builder
      .addCase(updateBusiness.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateBusiness.fulfilled, onMutationFulfilled)
      .addCase(updateBusiness.rejected, onMutationRejected);

    builder
      .addCase(deleteBusiness.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(deleteBusiness.fulfilled, onMutationFulfilled)
      .addCase(deleteBusiness.rejected, onMutationRejected);

    builder
      .addCase(updateRelatedEntity.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateRelatedEntity.fulfilled, onMutationFulfilled)
      .addCase(updateRelatedEntity.rejected, onMutationRejected);

    builder
      .addCase(deleteRelatedEntity.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(deleteRelatedEntity.fulfilled, onMutationFulfilled)
      .addCase(deleteRelatedEntity.rejected, onMutationRejected);

    const applyMiscMetadata = (state: ProfileMemoryState, data: MiscMetadataResponse | undefined): void => {
      const mem = data?.memory_information;
      if (mem) {
        state.miscMetadata.isMemoryModalShown = mem.is_memory_modal_shown ?? false;
        state.miscMetadata.isMemoryBannerShown = mem.is_memory_banner_shown ?? false;
        state.miscMetadata.hasSavedMemory = mem.has_saved_memory ?? false;
      }
    };
    builder
      .addCase(fetchMiscMetadata.pending, (state) => {
        state.miscMetadataStatus = "loading";
      })
      .addCase(fetchMiscMetadata.fulfilled, (state, action) => {
        state.miscMetadataStatus = "success";
        applyMiscMetadata(state, action.payload);
      })
      .addCase(fetchMiscMetadata.rejected, (state) => {
        state.miscMetadataStatus = "failed";
      });

    builder.addCase(patchMiscMetadata.fulfilled, (state, action) => {
      applyMiscMetadata(state, action.payload);
    });
  },
});

export const { setShowProfileMemoryPanel, updateVersionFromWS } = profileMemorySlice.actions;
export default profileMemorySlice.reducer;
